import React, {FC, ReactNode} from "react";
import {Button, Card, Col, Row} from "antd";
import {ArrowRightOutlined, CheckCircleFilled, CheckOutlined, ExclamationOutlined} from "@ant-design/icons";
import {blue, green} from "@ant-design/colors";
import {BookingType, ExecuteTransferMutation, TransferOldBookingTreatment, TransferSurplusHandling} from "../../../generated/graphql";
import {CurrencyShort, renderAmount} from "../../../helpers/renderAmount";
import formatDate from "../../../helpers/formatDate";
import {getTextColor} from "../../../helpers/getTextColor";
import i18n from "../../../services/i18n";
import RenderInvoicePreview from "../../../views/RenderInvoicePreview/RenderInvoicePreview";
import {Communication} from "./TransferWizardSteps";
import {CourseInfo, creditAfterNote} from "./TransferSummary";
import {paidInSourceNote} from "./transferCardRows";
import {bookingPriceOf, CostBreakdown} from "./TransferCostCard";

/**
 * Ergebnis-Dialog der Umbuchung (Entwurf docs/sketches/transfer-summary/result.html):
 * ein Satz mit Haken, darunter die Erledigt-Liste der Nacharbeit (Stornobeleg, Rechnung,
 * Mail, Erstattung, Nachrücken) mit Haken oder Warnung und Link, darunter die beiden Karten
 * mit den Endzahlen. Was schon in der Vorschau stand, wird nicht wiederholt.
 */

export type ExecuteResult = ExecuteTransferMutation["executeTransfer"];

type Props = {
    executed: ExecuteResult;
    attendeeFullName?: string;
    bookerName?: string;
    sourceLabel: string;
    sourceCourse: CourseInfo;
    targetCourse: CourseInfo;
    communication: Communication;
    currency?: CurrencyShort;
    onGoToCourse?: () => void;
    onGoToBooking: () => void;
};

const GREY = "rgba(0,0,0,0.65)";
const GREY_LIGHT = "rgba(0,0,0,0.45)";
const LINE = "1px solid #f0f0f0";

const Chip: FC<{label?: string; color?: string}> = ({label, color}) => label ? <span style={{background: color ?? blue[3], color: getTextColor({value: color ?? blue[3]}), borderRadius: 10, padding: "0 8px", fontSize: 12, fontWeight: 600, lineHeight: "20px", marginRight: 8}}>{label}</span> : null;

const Fact: FC<{label: ReactNode; sub?: ReactNode; strong?: boolean; muted?: boolean}> = ({label, sub, strong, muted, children}) => <div style={{display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, padding: "5px 0", borderBottom: LINE}}>
    <span style={{color: strong ? undefined : GREY, fontWeight: strong ? 600 : 400}}>{label}</span>
    <span style={{textAlign: "right"}}><span style={{fontWeight: strong ? 600 : 400, color: muted ? GREY_LIGHT : undefined}}>{children}</span>{sub && <div style={{color: GREY_LIGHT, fontSize: 12}}>{sub}</div>}</span>
</div>;

/** Eine Zeile der Erledigt-Liste: Haken oder Warnung, Text mit Unterzeile, rechts eine Aktion. */
const Done: FC<{warn?: boolean; sub?: ReactNode; action?: ReactNode}> = ({warn, sub, action, children}) => <div style={{display: "grid", gridTemplateColumns: "26px 1fr auto", gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: LINE}}>
    <span style={{width: 18, height: 18, borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, background: warn ? "#fffbe6" : "#f6ffed", border: `1px solid ${warn ? "#ffe58f" : "#b7eb8f"}`, color: warn ? "#d48806" : "#389e0d"}}>{warn ? <ExclamationOutlined /> : <CheckOutlined />}</span>
    <span><span style={{fontWeight: 500}}>{children}</span>{sub && <div style={{color: GREY_LIGHT, fontSize: 12}}>{sub}</div>}</span>
    <span>{action}</span>
</div>;

const TransferResult: FC<Props> = ({executed, attendeeFullName, bookerName, sourceLabel, sourceCourse, targetCourse, communication, currency, onGoToCourse, onGoToBooking}) => {
    const tr = i18n.containers.bookings.TransferWizard.resultDialog;
    const trSummary = i18n.containers.bookings.TransferWizard.summary;
    const money = (amount: number) => renderAmount({amount: Math.round(amount * 100) / 100, currency});
    const plan = executed.plan;
    const isWaitlist = executed.booking.bookingType === BookingType.Waitlist;
    const newLabel = `${executed.booking.bookingNumberPrefix}-${executed.booking.bookingNumber}`;
    const deleted = executed.oldBookingDeleted || plan.oldBooking.treatment === TransferOldBookingTreatment.Delete;
    const outcome = executed.surplusOutcome;
    // Gescheiterter Mollie-Refund: mehr Guthaben als geplant (wie die Umbuchungs-Mail des Servers).
    const creditAfter = outcome ? plan.creditAfter + (outcome.requestedRefund - outcome.refunded) : plan.creditAfter;
    // Rechnungsbetrag = Buchungspreis (Kurspreis + Posten − verrechnetes Guthaben), wie auf der Rechnung selbst.
    const total = bookingPriceOf(plan);
    const firstRate = plan.rates[0];
    const invoiceSub = firstRate ? (plan.rates.length > 1 ? tr.invoiceSub({amount: money(total), count: String(plan.rates.length), date: formatDate(new Date(String(firstRate.due)), "dd.MM.yyyy")}) : tr.invoiceSubOne({amount: money(total), date: formatDate(new Date(String(firstRate.due)), "dd.MM.yyyy")})) : money(total);
    const attachments = [communication.attachReversalInvoice && executed.reversalInvoice ? trSummary.reversalInvoice() : "", communication.attachInvoiceB && executed.invoiceB ? trSummary.invoice() : ""].filter(Boolean);
    const warnings = executed.warnings;
    const refundIds = outcome ? outcome.refunds.map((refund) => refund.mollieRefundId).filter(Boolean).join(", ") || outcome.mollieRefundId || "" : "";
    const course = targetCourse.number ?? "";

    const rows: Array<ReactNode> = [];

    if (executed.reversalInvoice) {
        rows.push(<Done key="reversal" sub={tr.reversalSub()} action={<RenderInvoicePreview invoiceData={{...executed.reversalInvoice, isReversalInvoice: true}} />}>{tr.reversalCreated({number: `${executed.reversalInvoice.invoiceNumberPrefix}-${executed.reversalInvoice.invoiceNumber}`})}</Done>);
    }
    if (executed.invoiceB) {
        rows.push(<Done key="invoice" sub={invoiceSub} action={<RenderInvoicePreview invoiceData={executed.invoiceB} />}>{tr.invoiceCreated({number: `${executed.invoiceB.invoiceNumberPrefix}-${executed.invoiceB.invoiceNumber}`})}</Done>);
    } else if (!isWaitlist && !communication.createInvoiceBNow) {
        rows.push(<Done key="invoice-later" sub={invoiceSub}>{tr.invoiceLater()}</Done>);
    }
    if (executed.mailSent) {
        rows.push(<Done key="mail" sub={attachments.length > 0 ? tr.mailAttachments({attachments: attachments.join(", ")}) : tr.mailNoAttachments()}>{tr.mailSent({booker: bookerName ?? ""})}</Done>);
    } else if (!communication.sendMail) {
        rows.push(<Done key="mail-skipped" sub={tr.mailSkipped()}>{tr.mailNotSent()}</Done>);
    }
    if (outcome && outcome.kind === TransferSurplusHandling.MollieRefund) {
        rows.push(outcome.refunded > 0
            ? <Done key="refund" sub={tr.refundMollieSub({refundId: refundIds, credit: money(outcome.creditRemaining)})}>{tr.refundMollie({amount: money(outcome.refunded)})}</Done>
            : <Done key="refund" warn sub={tr.refundMollieFailedSub({credit: money(outcome.creditRemaining)})}>{tr.refundMollieFailed()}</Done>);
    }
    if (outcome && outcome.kind === TransferSurplusHandling.ManualRefund) {
        rows.push(<Done key="refund-manual" sub={outcome.reason ?? undefined}>{tr.refundManual({amount: money(plan.surplus)})}</Done>);
    }
    if (executed.promotedBookingId) {
        rows.push(<Done key="promoted">{tr.promoted({booking: executed.promotedBookingId})}</Done>);
    }
    // Nacharbeiten, die nach dem Commit fehlgeschlagen sind — als Warnzeilen, nicht als Fehler: die Umbuchung ist verbucht.
    warnings.forEach((warning, index) => rows.push(<Done key={`warning-${index}`} warn>{warning}</Done>));

    const subtitleTail = warnings.length > 0
        ? <b>{warnings.length === 1 ? tr.followUpOne() : tr.followUp({count: String(warnings.length)})}</b>
        : executed.mailSent ? tr.mailInformed({booker: bookerName ?? ""}) : "";

    return <div style={{textAlign: "left"}}>
        <div style={{display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16}}>
            <CheckCircleFilled style={{fontSize: 40, color: green[6], flex: "none"}} />
            <div>
                <div style={{fontSize: 18, fontWeight: 600, lineHeight: 1.3}}>{isWaitlist ? tr.titleWaitlist({attendee: attendeeFullName ?? "", course}) : tr.title({attendee: attendeeFullName ?? "", course})}</div>
                <div style={{color: GREY, marginTop: 4}}>{tr.subtitle({newBooking: newLabel, oldBooking: sourceLabel, oldOutcome: deleted ? tr.oldDeleted() : tr.oldCanceled()})}{" · "}{subtitleTail}</div>
            </div>
        </div>
        {rows.length > 0 && <div style={{border: "1px solid #d9d9d9", borderRadius: 6, marginBottom: 16}}>{rows}</div>}
        <Row gutter={0} align="top">
            <Col span={11}>
                <Card size="small" title={trSummary.oldBooking()} extra={<b>{sourceLabel}</b>} style={{background: "#fafafa"}} headStyle={{color: GREY, fontSize: 13, background: "#fafafa"}}>
                    <div style={{fontSize: 15, fontWeight: 600, marginBottom: 6}}><Chip label={sourceCourse.typeName} color={sourceCourse.typeColor} />{sourceCourse.number}</div>
                    <Fact label={tr.status()}>{deleted ? tr.statusDeleted() : tr.statusCanceled()}</Fact>
                    <Fact label={trSummary.paid()} sub={paidInSourceNote(plan, money)}>{money(plan.paidA)}</Fact>
                    <Fact label={tr.place()} sub={executed.promotedBookingId ? undefined : tr.placeNobodyPromoted()}>{tr.placeFree()}</Fact>
                </Card>
            </Col>
            <Col span={2} style={{display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "stretch"}}><ArrowRightOutlined style={{fontSize: 22, color: blue[5]}} /></Col>
            <Col span={11}>
                <Card size="small" title={<span style={{color: blue[5]}}>{trSummary.newBooking()}</span>} extra={<b>{newLabel}</b>} style={{borderColor: "#a9c4ff"}} headStyle={{fontSize: 13, background: "#eef4ff", borderBottomColor: "#d6e3ff"}}>
                    <div style={{fontSize: 15, fontWeight: 600, marginBottom: 6}}><Chip label={targetCourse.typeName} color={targetCourse.typeColor} />{targetCourse.number}</div>
                    {/* Dieselbe Kostenaufstellung wie in der Zusammenfassung (Kurspreis, Posten, Guthaben, Buchungspreis, Übertrag, Ratenplan, Summe). */}
                    <CostBreakdown plan={plan} sourceLabel={sourceLabel} currency={currency} waitlist={isWaitlist} />
                    <Fact label={tr.credit()} sub={creditAfterNote(plan, sourceLabel, money, {summary: trSummary}) ?? (plan.surplus <= 0 && creditAfter > 0 ? tr.creditUnchanged() : undefined)}>{money(creditAfter)}</Fact>
                </Card>
            </Col>
        </Row>
        {plan.hints.length > 0 && <ul style={{margin: "12px 0 0", paddingLeft: 18, color: GREY_LIGHT, fontSize: 12}}>{plan.hints.map((hint, index) => <li key={index}>{hint}</li>)}</ul>}
        <div style={{marginTop: 12}}>
            {onGoToCourse && <Button type="link" style={{paddingLeft: 0}} onClick={onGoToCourse}>{tr.toCourse({course})}</Button>}
            <Button type="link" onClick={onGoToBooking}>{tr.toBooking({booking: newLabel})}</Button>
        </div>
    </div>;
};

export default TransferResult;
